import React from "react";
import {
  Container,
  Header,
  Segment,
  Button,
  Table,
  Message,
  Loader,
  Dimmer,
  Icon,
  Input,
  Divider,
  Popup,
} from "semantic-ui-react";
import { configurableApi } from "@api/configurables/configurables";

export class Configurables extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      configurables: [],
      loading: true,
      saving: false,
      error: null,
      success: null,
      editingId: null,
      editValue: "",
      newConfigurable: {
        name: "",
        value: "",
        description: "",
      },
    };
  }

  componentDidMount() {
    this.fetchConfigurables();
  }

  fetchConfigurables = async () => {
    try {
      this.setState({ loading: true, error: null });
      const response = await configurableApi.getAll();
      if (response && response.data) {
        const configurables = response.data.hits || response.data || [];
        this.setState({
          configurables: configurables,
          loading: false,
        });
      } else {
        this.setState({
          error: "No data received from server",
          loading: false,
        });
      }
    } catch (error) {
      this.setState({
        error: error.message || "Failed to fetch configurables",
        loading: false,
      });
    }
  };

  handleEdit = (configurable) => {
    this.setState({
      editingId: configurable.id,
      editValue: configurable.value,
    });
  };

  handleCancelEdit = () => {
    this.setState({
      editingId: null,
      editValue: "",
    });
  };

  handleSaveEdit = async (id) => {
    const { editValue } = this.state;
    try {
      this.setState({ saving: true });
      const configurable = this.state.configurables.find((c) => c.id === id);
      if (!configurable) {
        this.setState({
          error: "Configuration not found",
          saving: false,
        });
        return;
      }
      const response = await configurableApi.updateValue(id, editValue);
      if (response && (response.status === "success" || response.data)) {
        this.setState((prevState) => ({
          configurables: prevState.configurables.map((c) =>
            c.id === id ? { ...c, value: editValue } : c
          ),
          editingId: null,
          editValue: "",
          success: "Configuration updated successfully!",
          saving: false,
        }));
        setTimeout(() => {
          this.setState({ success: null });
        }, 3000);
      } else {
        this.setState({
          error: response?.message || "Failed to update configuration",
          saving: false,
        });
      }
    } catch (error) {
      this.setState({
        error: error.message || "Failed to update configuration",
        saving: false,
      });
    }
  };

  handleAddNew = async () => {
    const { newConfigurable } = this.state;
    if (!newConfigurable.name.trim() || !newConfigurable.value.trim()) {
      this.setState({ error: "Name and value are required" });
      return;
    }
    try {
      this.setState({ saving: true });
      const response = await configurableApi.create(newConfigurable);
      if (response && (response.status === "success" || response.data)) {
        this.setState({
          newConfigurable: { name: "", value: "", description: "" },
          success: "Configuration added successfully!",
          saving: false,
        });
        await this.fetchConfigurables();
        setTimeout(() => {
          this.setState({ success: null });
        }, 3000);
      } else {
        this.setState({
          error: response?.message || "Failed to add configuration",
          saving: false,
        });
      }
    } catch (error) {
      this.setState({
        error: error.message || "Failed to add configuration",
        saving: false,
      });
    }
  };

  renderConfigurableRow = (configurable) => {
    const { editingId, editValue, saving } = this.state;
    const isEditing = editingId === configurable.id;

    return (
      <Table.Row key={configurable.id}>
        <Table.Cell>
          <strong>
            {configurable.name
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}
          </strong>
        </Table.Cell>
        <Table.Cell>
          {isEditing ? (
            <Input
              value={editValue}
              onChange={(e) => this.setState({ editValue: e.target.value })}
              loading={saving}
              disabled={saving}
              fluid
            />
          ) : (
            <span style={{ whiteSpace: "pre-wrap" }}>{configurable.value}</span>
          )}
        </Table.Cell>
        <Table.Cell>
          {configurable.name === "current_academic_year"
            ? "Set the current academic year"
            : configurable.description || "No description"}
        </Table.Cell>
        <Table.Cell collapsing>
          {isEditing ? (
            <>
              <Popup
                content="Save changes"
                position="top center"
                trigger={
                  <Button
                    icon
                    color="green"
                    onClick={() => this.handleSaveEdit(configurable.id)}
                    loading={saving}
                    disabled={saving}
                  >
                    <Icon name="check" />
                  </Button>
                }
              />
              <Popup
                content="Cancel editing"
                position="top center"
                trigger={
                  <Button
                    icon
                    color="grey"
                    onClick={this.handleCancelEdit}
                    disabled={saving}
                  >
                    <Icon name="close" />
                  </Button>
                }
              />
            </>
          ) : (
            <Popup
              content="Edit configuration"
              position="top center"
              trigger={
                <Button
                  icon
                  color="blue"
                  onClick={() => this.handleEdit(configurable)}
                  disabled={saving}
                >
                  <Icon name="edit" />
                </Button>
              }
            />
          )}
        </Table.Cell>
      </Table.Row>
    );
  };

  render() {
    const { configurables, loading, error, success } = this.state;

    return (
      <Container
        style={{ padding: "2rem 0", width: "100%", maxWidth: "1100px" }}
      >
        <Segment raised style={{ padding: "2rem" }}>
          <Header
            as="h1"
            textAlign="center"
            style={{ margin: 0, color: "#225745" }}
          >
            <Icon name="settings" />
            <Header.Content>Configurable Information Management</Header.Content>
          </Header>

          <Divider />

          {error && (
            <Message
              negative
              onDismiss={() => this.setState({ error: null })}
              style={{ marginBottom: "1.5rem" }}
            >
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          {success && (
            <Message
              positive
              onDismiss={() => this.setState({ success: null })}
              style={{ marginBottom: "1.5rem" }}
            >
              <Message.Header>Success</Message.Header>
              <p>{success}</p>
            </Message>
          )}

          {loading ? (
            <Dimmer active inverted>
              <Loader>Loading configurations...</Loader>
            </Dimmer>
          ) : configurables.length === 0 ? (
            <Message info>
              <Message.Header>No Configurations Found</Message.Header>
              <p>Start by adding your first configuration.</p>
            </Message>
          ) : (
            <Table celled striped compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={3}>Name</Table.HeaderCell>
                  <Table.HeaderCell width={5}>Value</Table.HeaderCell>
                  <Table.HeaderCell width={6}>Description</Table.HeaderCell>
                  <Table.HeaderCell collapsing>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {configurables.map(this.renderConfigurableRow)}
              </Table.Body>
            </Table>
          )}
        </Segment>
      </Container>
    );
  }
}

export default Configurables;
